// countryApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from 'model/response-model';
import { AddEnterprisePlanModel, DeleteEnterpriseModel, EnterpriseItem, EnterprisePlanQuery, EnterprisePlanResponseModel, ExistingEnterpriseModel, ExternalResponse, GenerateLinkResponse, GeneratePaymentLink, GetDetailsResponse, GetPaymentDetailsQuery, MakePaymentResponse, PaymentDetails, RenewalInititateModel, UserModel, ValidatePayment } from 'model/enterprise-plan-model';
import { baseQueryWithReauth, baseQueryWithReauthExternal, baseQueryWithReauthTenant } from './config/interceptor';

export const enterprisePlanApi = createApi({
  reducerPath: 'enterprisePlanApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['EnterprisePlan'],
  endpoints: (builder) => ({
    createEnterprisePlan: builder.mutation<ApiResponse<string>, AddEnterprisePlanModel>({
      query: (model) => ({
        url: 'EnterpricePlan/create-plan',
        method: 'POST',
        body: model
      })
    }),
    creatExistingEnterprisePlan: builder.mutation<ApiResponse<string>, ExistingEnterpriseModel>({
      query: (model) => ({
        url: 'EnterpricePlan/create-plan-existing-company',
        method: 'POST',
        body: model
      })
    }),
    updateEnterprisePlan: builder.mutation<ApiResponse<string>, any>({
      query: (model) => ({
        url: 'EnterpricePlan/edit-plan',
        method: 'PUT',
        body: model
      })
    }),
    getEnterprisePlan: builder.query<ApiResponse<EnterpriseItem>, EnterprisePlanQuery>({
      query: (params) => ({
        url: 'EnterpricePlan/get-enterprice-plan',
        params: params,
      }),
    }),
    deleteEnterprise: builder.mutation<ApiResponse<string>, DeleteEnterpriseModel>({
      query: (model) => ({
        url: `EnterpricePlan/delete-plan/${model?.tenantId}/${model?.enterPriseSubId}`,
        method: 'DELETE',
      }),
    })
  }),
});

export const { 
  useCreateEnterprisePlanMutation, 
  useCreatExistingEnterprisePlanMutation, 
  useGetEnterprisePlanQuery, 
  useDeleteEnterpriseMutation, 
  useUpdateEnterprisePlanMutation
} = enterprisePlanApi;



export const externalEnterprise = createApi({
  reducerPath: 'externalEnterpriseApi',
  baseQuery: baseQueryWithReauthExternal,
  tagTypes: ['EnterprisePaymentLink'],
  endpoints: (builder) => ({
    createEnterpriseSub: builder.mutation<ExternalResponse<string>, UserModel>({
      query: (model) => ({
        url: 'subscription/onboardandcreateenterprisesub',
        method: 'POST',
        body: model
        }),
    }),
    createEnterpriseSubForExistingUser: builder.mutation<ExternalResponse<string>, UserModel>({
      query: (model) => ({
        url: 'subscription/onboardandcreateenterprisesubforexistinguser',
        method: 'POST',
        body: model
        }),
    }),
    generateEnterprisePayment: builder.mutation<ExternalResponse<GenerateLinkResponse>, GeneratePaymentLink>({
      query: (model) => ({
        url: 'subscription/createenterprisepaymentlink',
        method: 'POST',
        body: model
      })
    }),
    validateEnterprisePayment: builder.mutation<ExternalResponse<MakePaymentResponse>, ValidatePayment>({
      query: (model) => ({
        url: 'subscription/makeenterprisepayment',
        method: 'POST',
        params: model
      })
    }),
    getEnterprisePaymentDetails: builder.query<ExternalResponse<GetDetailsResponse>, GetPaymentDetailsQuery>({
      query: (params) => ({
        url: 'subscription/getenterprisepaymentdetails',
        params: params,
      }),
    }),
    renewEnterpriseSub: builder.mutation<ExternalResponse<GenerateLinkResponse>, RenewalInititateModel>({
      query: (params) => ({
        url: `subscription/initaiteenterprisepaymentforrenewal`,
        method: 'POST',
        params: params
        })
    }),
    activateEnterprisesubscription: builder.mutation<ExternalResponse<null>, RenewalInititateModel>({
      query: (params) => ({
        url: `subscription/activateenterprisesubscription`,
        method: 'POST',
        params: params
      })
    }),
    getPlans: builder.query<ExternalResponse<any>, any>({
      query: (data) => ({
        url: 'subscription/getplans',
        method: 'GET',
        params: data
        })
    })
  })
});

export const {
  useCreateEnterpriseSubMutation,
  useCreateEnterpriseSubForExistingUserMutation,
  useGenerateEnterprisePaymentMutation,
  useValidateEnterprisePaymentMutation,
  useGetEnterprisePaymentDetailsQuery,
  useRenewEnterpriseSubMutation,
  useActivateEnterprisesubscriptionMutation,
  useGetPlansQuery
} = externalEnterprise;

export const tenantApi = createApi({
  reducerPath: 'tenantApi',
  baseQuery: baseQueryWithReauthTenant,
  tagTypes: ['Tenant'],
  endpoints: (builder) => ({
    getSuspendedTenants: builder.query<ApiResponse<any>, { subdomain?: string } | void>({
      query: (model) => ({
        url: 'tenant/getsuspendedtenants',
        method: 'GET',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    getDeletedTenants: builder.query<ApiResponse<any>, void>({
      query: () => ({
        url: 'tenant/getdeletedtenants',
        method: 'GET',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    suspendTenant: builder.mutation<ApiResponse<string>, { subdomain: string }>({
      query: (model) => ({
        url: `tenant/suspendtenant/${model.subdomain}`,
        method: 'PUT',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    reactivateTenant: builder.mutation<ApiResponse<string>, { subdomain: string }>({
      query: (model) => ({
        url: `tenant/reactivatetenant/${model.subdomain}`,
        method: 'PUT',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    deleteTenant: builder.mutation<ApiResponse<string>, { subdomain: string, deletedBy: string, deletionReason: string }>({
      query: (model) => ({
        url: `tenant/deletetenant/${model.subdomain}?deletedBy=${model.deletedBy}&deletionReason=${model.deletionReason}`,
        method: 'DELETE',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    getTenantOverview: builder.query<ApiResponse<any>, string>({
      query: (subdomain) => ({
        url: `tenant/gettenantoverview/${subdomain}`,
        method: 'GET',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    getAllTenants: builder.query<ApiResponse<any>, void>({
      query: () => ({
        url: `tenant/getalltenants`,
        method: 'GET',
        headers: {
          'Subdomain': 'joble'
        }
      }),
    }),
    impersonateTenant: builder.mutation<{ Code: string; ExpiresInSeconds: number }, string>({
      query: (subdomain) => ({
        url: `Identity/ImpersonateTenant/${subdomain}`,
        method: 'POST',
      }),
    }),
    inviteExternalUsers: builder.mutation<ApiResponse<any>, { invites: { Email: string, Application: string, Country: string }[], resendInvite: boolean, subdomain: string }>({
      query: (model) => ({
        url: `TeamSheet/InviteExternalUsers?resendInvite=${model.resendInvite}`,
        method: 'POST',
        headers: {
          'subdomain': model.subdomain
        },
        body: model.invites
      }),
    }),
  })
});

export const {
  useGetSuspendedTenantsQuery,
  useGetDeletedTenantsQuery,
  useSuspendTenantMutation,
  useReactivateTenantMutation,
  useGetTenantOverviewQuery,
  useDeleteTenantMutation,
  useGetAllTenantsQuery,
  useImpersonateTenantMutation,
  useInviteExternalUsersMutation
} = tenantApi;